import { apiGetV2 } from "~/shared/api/client";
import { downloadFromApi } from "~/shared/lib/export-utils";

import { mapAssetProvenance } from "./lib/asset-provenance-mapper";
import type { AssetListResponse, AssetProvenance, ListAssetsParams } from "./types";

type AssetQueryFilters = Omit<ListAssetsParams, "workspaceId" | "page" | "pageSize">;

/**
 * Общий query-билд для обоих листингов ассетов: фильтры → snake_case query-параметры,
 * 1-based `page` → offset/limit (бэк пагинирует offset/limit).
 */
function toAssetQuery(
    filters: AssetQueryFilters,
    page?: number,
    pageSize?: number,
): Record<string, string | number | undefined> {
    const limit = pageSize ?? 50;
    const offset = page && page > 1 ? (page - 1) * limit : 0;

    return {
        type: filters.type,
        model_name: filters.modelName,
        mode: filters.mode,
        creator_id: filters.creatorId,
        project_id: filters.projectId,
        scene_id: filters.sceneId,
        created_from: filters.createdFrom,
        created_to: filters.createdTo,
        sort_by: filters.sortBy,
        order: filters.order,
        limit,
        offset,
    };
}

/**
 * Общий fetch обоих листингов: `workspace_id` уходит ЗАГОЛОВКОМ `X-Workspace-Id` (третий
 * аргумент `apiGetV2` — `RequestContext`), фильтры/сорт/страница — query-параметрами. Путь
 * — единственное, что различает workspace- и project-скоупы.
 */
function fetchAssets(path: string, params: ListAssetsParams): Promise<AssetListResponse> {
    const { workspaceId, page, pageSize, ...filters } = params;
    return apiGetV2<AssetListResponse>(path, toAssetQuery(filters, page, pageSize), { workspaceId });
}

/** Листинг ассетов workspace (`GET /v2/assets`). Серверная фильтрация/сортировка/пагинация. */
export function listAssets(params: ListAssetsParams = {}): Promise<AssetListResponse> {
    return fetchAssets("/assets", params);
}

/**
 * Листинг ассетов проекта (`GET /v2/projects/{id}/assets`). `projectId` — в пути (не в query;
 * фильтр по проекту в этом скоупе не имеет смысла).
 */
export function listProjectAssets(
    projectId: string,
    params: ListAssetsParams = {},
): Promise<AssetListResponse> {
    return fetchAssets(`/projects/${projectId}/assets`, params);
}

/** Provenance detail for one asset (`GET /v2/assets/{id}/provenance`). */
export function getAssetProvenance(assetId: string, workspaceId?: string): Promise<AssetProvenance> {
    return apiGetV2<unknown>(`/assets/${assetId}/provenance`, undefined, { workspaceId }).then(
        mapAssetProvenance,
    );
}

/** Download the JSON returned by the only supported provenance export route. */
export function exportAssetProvenance(
    assetId: string,
    filename: string,
    workspaceId?: string,
): Promise<void> {
    return downloadFromApi(`/v2/assets/${assetId}/provenance/export`, filename, { workspaceId });
}
